import { isRecord } from '@tadpoles/common';
import { mapRxDbFieldName, mapRxDbPrimaryKey } from './rxdb-reserved-fields.js';
const defaultPrimaryKeyMaxLength = 200;
function mergeObjectSchema(schema) {
    const allOfValue = schema['allOf'];
    if (Array.isArray(allOfValue)) {
        return allOfValue.reduce((mergedSchema, part) => {
            if (!isRecord(part) || Array.isArray(part)) {
                return mergedSchema;
            }
            const resolvedPart = mergeObjectSchema(part);
            const mergedSchemaProperties = readSchemaProperties(mergedSchema);
            const resolvedPartProperties = readSchemaProperties(resolvedPart);
            const mergedProperties = {
                ...mergedSchemaProperties,
                ...resolvedPartProperties
            };
            const mergedRequired = [
                ...new Set([
                    ...readSchemaRequired(mergedSchema),
                    ...readSchemaRequired(resolvedPart)
                ])
            ];
            return {
                ...mergedSchema,
                ...resolvedPart,
                type: 'object',
                properties: mergedProperties,
                required: mergedRequired
            };
        }, { type: 'object', properties: {}, required: [] });
    }
    const anyOfValue = schema['anyOf'];
    if (Array.isArray(anyOfValue)) {
        return anyOfValue.reduce((mergedSchema, part, index) => {
            if (!isRecord(part) || Array.isArray(part)) {
                return mergedSchema;
            }
            const resolvedPart = mergeObjectSchema(part);
            const mergedSchemaProperties = readSchemaProperties(mergedSchema);
            const resolvedPartProperties = readSchemaProperties(resolvedPart);
            const mergedProperties = mergeUnionProperties(mergedSchemaProperties, resolvedPartProperties);
            const resolvedRequired = readSchemaRequired(resolvedPart);
            const mergedRequired = index === 0
                ? resolvedRequired
                : readSchemaRequired(mergedSchema).filter((fieldName) => resolvedRequired.includes(fieldName));
            return {
                ...mergedSchema,
                ...resolvedPart,
                type: 'object',
                properties: mergedProperties,
                required: mergedRequired
            };
        }, { type: 'object', properties: {}, required: [] });
    }
    return schema;
}
function mergeUnionProperties(existingProperties, nextProperties) {
    return Object.fromEntries([...new Set([...Object.keys(existingProperties), ...Object.keys(nextProperties)])].map((fieldName) => {
        const existingProperty = existingProperties[fieldName];
        const nextProperty = nextProperties[fieldName];
        if (isRecord(existingProperty) && !Array.isArray(existingProperty) && isRecord(nextProperty) && !Array.isArray(nextProperty)) {
            return [fieldName, mergeUnionProperty(existingProperty, nextProperty)];
        }
        return [fieldName, nextProperty ?? existingProperty];
    }));
}
function mergeUnionProperty(existingProperty, nextProperty) {
    const mergedProperty = {
        ...existingProperty,
        ...nextProperty
    };
    const existingType = existingProperty['type'];
    const nextType = nextProperty['type'];
    const existingConst = existingProperty['const'];
    const nextConst = nextProperty['const'];
    if (existingType === nextType && existingConst !== undefined && nextConst === undefined) {
        const propertyWithoutConst = { ...mergedProperty };
        delete propertyWithoutConst['const'];
        return propertyWithoutConst;
    }
    if (existingType === nextType && existingConst === undefined && nextConst !== undefined) {
        const propertyWithoutConst = { ...mergedProperty };
        delete propertyWithoutConst['const'];
        return propertyWithoutConst;
    }
    return mergedProperty;
}
export function createRxDbSchema(definition) {
    const clonedSchema = JSON.parse(JSON.stringify(definition.modelSchema));
    if (!isRecord(clonedSchema)) {
        throw new Error(`Persistence collection "${definition.kind}" modelSchema must clone to an object`);
    }
    const rawSchema = mergeObjectSchema(clonedSchema);
    const propertiesValue = rawSchema['properties'];
    const requiredValue = rawSchema['required'];
    if (!isRecord(propertiesValue) || Array.isArray(propertiesValue)) {
        throw new Error(`Persistence collection "${definition.kind}" must define object properties`);
    }
    const properties = Object.fromEntries(Object.entries(propertiesValue)
        .filter((entry) => {
        const [, value] = entry;
        return isRecord(value);
    })
        .map(([fieldName, value]) => [mapRxDbFieldName(fieldName), value]));
    const required = Array.isArray(requiredValue)
        ? [
            ...new Set(requiredValue
                .filter((value) => typeof value === 'string')
                .map((fieldName) => mapRxDbFieldName(fieldName)))
        ]
        : [];
    const primaryKey = mapRxDbPrimaryKey(definition.primaryKey);
    const primaryKeyField = typeof primaryKey === 'string' ? primaryKey : primaryKey.key;
    const primaryKeyProperty = properties[primaryKeyField];
    if (!primaryKeyProperty) {
        throw new Error(`Persistence collection "${definition.kind}" is missing primary key field "${primaryKeyField}" in modelSchema`);
    }
    if (primaryKeyProperty.type !== 'string') {
        throw new Error(`Persistence collection "${definition.kind}" primary key field "${primaryKeyField}" must be a string schema`);
    }
    if (typeof primaryKeyProperty.maxLength !== 'number') {
        primaryKeyProperty.maxLength = defaultPrimaryKeyMaxLength;
    }
    if (!required.includes(primaryKeyField)) {
        required.push(primaryKeyField);
    }
    return {
        ...rawSchema,
        version: readSchemaVersion(rawSchema),
        primaryKey,
        type: 'object',
        properties,
        required,
        indexes: definition.indexes?.map((index) => mapRxDbFieldName(index))
    };
}
function readSchemaProperties(schema) {
    const properties = schema['properties'];
    if (!isRecord(properties) || Array.isArray(properties)) {
        return {};
    }
    return properties;
}
function readSchemaRequired(schema) {
    const required = schema['required'];
    return Array.isArray(required)
        ? required.filter((value) => typeof value === 'string')
        : [];
}
function readSchemaVersion(schema) {
    const version = schema['version'];
    return typeof version === 'number' ? version : 0;
}
